import { useEffect, useState } from 'react';
import { Badge, Button, Group, NumberInput, Paper, Select, SimpleGrid, Stack, Text, Textarea } from '@mantine/core';
import { IconChecklist, IconClock, IconNotes, IconPencil, IconReceipt } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import type { CustomerContractModel } from '../../lib/api/domains/weclapp';
import { FairUseModal } from './FairUseModal';
import { OpeningHoursModal } from '../../components/customer/openingHours/OpeningHoursModal';
import { CustomerOpeningStatusBadge } from '../../components/customer/openingHours/CustomerOpeningStatusBadge';
import { PortfolioMatchModal } from './PortfolioMatchModal';
import { InfoCard, LabelWithHint } from './CustomerInfoParts';
import { useCustomerCare, type CustomerInfoIdent } from './customerInfoQueries';

/**
 * Die vier Möglichkeiten in der Reihenfolge, in der man sie liest: erst „nicht
 * gepflegt", dann die drei echten Modelle. Die Beschriftungen entsprechen denen im
 * Cockpit-Hinweis — zwei Namen für dieselbe Sache wären eine Fehlerquelle.
 */
const CONTRACT_MODEL_OPTIONS: { value: CustomerContractModel; label: string }[] = [
  { value: 'UNKNOWN', label: 'Nicht hinterlegt' },
  { value: 'FLAT_RATE', label: 'Flatrate' },
  { value: 'HOURLY', label: 'Nach Aufwand' },
  { value: 'MIXED', label: 'Pauschale + Aufwand' },
];

/** Leicht abgesetzte Fläche innerhalb einer Karte. */
const INSET = { backgroundColor: 'var(--mantine-color-default-hover)' };

/**
 * Besonderheiten — steht ganz oben in den Kundeninfos, weil es das Erste ist, was ein
 * Techniker vor einem Einsatz wissen muss.
 */
export function ServiceNotesCard({ ident }: { ident: CustomerInfoIdent }) {
  const queryClient = useQueryClient();
  const { data, queryKey } = useCustomerCare(ident);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data && !dirty) setNotes(data.customer.serviceNotes ?? '');
  }, [data, dirty]);

  const mutation = useMutation({
    mutationFn: () => api.updateCustomerServiceNotes(data!.customer.id, notes),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey });
      notifications.show({ title: 'Besonderheiten gespeichert', message: '', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  if (!data) return null;

  return (
    <InfoCard
      icon={<IconNotes size={15} />}
      color="cyberOrange"
      title="Besonderheiten"
      actions={
        dirty ? (
          <>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => {
                setNotes(data.customer.serviceNotes ?? '');
                setDirty(false);
              }}
            >
              Verwerfen
            </Button>
            <Button size="compact-xs" loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Speichern
            </Button>
          </>
        ) : undefined
      }
    >
      <Textarea
        aria-label="Besonderheiten"
        placeholder={'Servicehinweise für Techniker: Zutritt, Ansprechzeiten, Umgebung, „vor Ort anmelden bei …"'}
        value={notes}
        onChange={(e) => {
          setNotes(e.currentTarget.value);
          setDirty(true);
        }}
        autosize
        minRows={2}
        maxRows={10}
      />
    </InfoCard>
  );
}

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * Öffnungszeiten — wann beim Kunden jemand da ist. Oben der Status wie an Ticket und
 * Chat, darunter der Wochenplan, damit man ihn ohne Dialog nachlesen kann.
 */
export function OpeningHoursCard({ ident }: { ident: CustomerInfoIdent }) {
  const { data, queryKey } = useCustomerCare(ident);
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const { openingHours: hours, openingStatus: status } = data.customer;
  const upcoming = (hours?.closures ?? [])
    .filter((c) => c.to >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.from.localeCompare(b.from));
  const fmt = (key: string) => key.split('-').reverse().join('.');

  return (
    <InfoCard
      icon={<IconClock size={15} />}
      color="teal"
      title="Öffnungszeiten"
      hint="Wann beim Kunden jemand erreichbar ist. Daraus zeigt der Support an Ticket, Chat und Rückruf, ob gerade geöffnet ist. Nicht die Servicezeit — die steht unter Fair Use und treibt die SLA-Uhr."
      actions={
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconPencil size={12} />}
          onClick={() => setOpen(true)}
        >
          {hours ? 'Bearbeiten' : 'Hinterlegen'}
        </Button>
      }
    >
      {hours ? (
        <Stack gap="xs">
          <CustomerOpeningStatusBadge status={status} size="md" />
          <Stack gap={2}>
            {hours.days.map((day, i) => (
              <Group key={i} gap="xs" wrap="nowrap">
                <Text size="xs" c="dimmed" w={24}>
                  {WEEKDAY_SHORT[i]}
                </Text>
                <Text size="xs" c={day.length ? undefined : 'dimmed'}>
                  {day.length ? day.map((iv) => `${iv.from}–${iv.to}`).join(', ') : 'geschlossen'}
                </Text>
              </Group>
            ))}
          </Stack>
          {upcoming.length > 0 && (
            <Text size="xs">
              <b>Geschlossen:</b>{' '}
              {upcoming
                .slice(0, 3)
                .map((c) => `${fmt(c.from)}${c.to !== c.from ? `–${fmt(c.to)}` : ''}${c.label ? ` (${c.label})` : ''}`)
                .join(', ')}
              {upcoming.length > 3 ? ` +${upcoming.length - 3}` : ''}
            </Text>
          )}
          {hours.note && (
            <Paper p="xs" radius="sm" style={INSET}>
              <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                {hours.note}
              </Text>
            </Paper>
          )}
        </Stack>
      ) : (
        <Text size="xs" c="dimmed">
          Noch nicht hinterlegt. Dann sieht der Support an Tickets und Chats, ob beim Kunden gerade jemand da
          ist, in Pause ist oder wann wieder geöffnet wird.
        </Text>
      )}
      <OpeningHoursModal
        opened={open}
        onClose={() => setOpen(false)}
        customerId={data.customer.id}
        customerName={data.customer.name}
        value={hours}
        queryKey={queryKey}
      />
    </InfoCard>
  );
}

/**
 * Vertragsmodell, Kontingent und Fair Use — alles, was festlegt, wie viel Betreuung in
 * der Vereinbarung steckt. Das Vertragsmodell steht vorn, weil es die gröbere Frage ist.
 */
export function ContractCareCard({ ident }: { ident: CustomerInfoIdent }) {
  const queryClient = useQueryClient();
  const { data, queryKey } = useCustomerCare(ident);
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  /**
   * Eingeplantes Kontingent aus der Pauschale. Leer = keins vereinbart; dann kommt
   * das Thema im Rückblick „Unsere IT" gar nicht erst vor, statt gegen eine Null
   * gerechnet zu werden.
   */
  const [contingent, setContingent] = useState<number | ''>('');
  const [contingentDirty, setContingentDirty] = useState(false);
  useEffect(() => {
    if (data && !contingentDirty) setContingent(data.customer.includedHoursPerMonth ?? '');
  }, [data, contingentDirty]);
  const [fairUseOpen, setFairUseOpen] = useState(false);

  const contingentMutation = useMutation({
    mutationFn: () =>
      api.updateCustomerContingent(data!.customer.id, contingent === '' ? null : Number(contingent)),
    onSuccess: () => {
      setContingentDirty(false);
      invalidate();
      notifications.show({ title: 'Kontingent gespeichert', message: '', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  /**
   * Vertragsmodell. Steuert nicht die Abrechnung, sondern den Hinweis, den der Techniker
   * im Ticket-Cockpit sieht — deshalb speichert es sofort beim Umschalten, ohne eigenen
   * Speichern-Knopf: Es ist eine Auswahl aus vier Möglichkeiten, kein Formular.
   */
  const contractModelMutation = useMutation({
    mutationFn: (model: CustomerContractModel) => api.updateCustomerContractModel(data!.customer.id, model),
    onSuccess: () => {
      invalidate();
      notifications.show({ title: 'Vertragsmodell gespeichert', message: '', color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  if (!data) return null;
  const customer = data.customer;

  return (
    <InfoCard icon={<IconReceipt size={15} />} color="blue" title="Vertrag & Betreuung">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <Select
            label={
              <LabelWithHint
                label="Vertragsmodell"
                hint={
                  'Der Techniker sieht daraus im Ticket, worauf er achten soll — bei einer Pauschale ' +
                  'nachhaltig statt ausufernd, nach Aufwand gründlich statt trödelnd. ' +
                  '„Nicht hinterlegt" blendet den Hinweis aus.'
                }
              />
            }
            data={CONTRACT_MODEL_OPTIONS}
            value={customer.contractModel ?? 'UNKNOWN'}
            onChange={(value) => value && contractModelMutation.mutate(value as CustomerContractModel)}
            disabled={contractModelMutation.isPending}
            allowDeselect={false}
            comboboxProps={{ zIndex: 1100 }}
          />
          <NumberInput
            label={
              <LabelWithHint
                label="Kontingent"
                hint={
                  'In der Pauschale eingeplante Betreuungsstunden. Grundlage für die Auslastung im ' +
                  'Rückblick „Unsere IT" — dort intern, beim Kunden nur, wenn ein Techniker es freigibt. ' +
                  'Leer = kein Kontingent vereinbart.'
                }
              />
            }
            value={contingent}
            onChange={(value) => {
              setContingent(typeof value === 'number' ? value : '');
              setContingentDirty(true);
            }}
            min={0}
            max={10000}
            step={0.5}
            decimalScale={2}
            suffix=" h/Monat"
            placeholder="nicht vereinbart"
          />
        </SimpleGrid>

        {contingentDirty && (
          <Group justify="flex-end" gap="xs">
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => {
                setContingent(customer.includedHoursPerMonth ?? '');
                setContingentDirty(false);
              }}
            >
              Verwerfen
            </Button>
            <Button size="compact-xs" loading={contingentMutation.isPending} onClick={() => contingentMutation.mutate()}>
              Kontingent speichern
            </Button>
          </Group>
        )}

        <Paper radius="sm" p="xs" style={INSET}>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <div style={{ minWidth: 0 }}>
              <Group gap={6} wrap="wrap">
                <Text size="xs" fw={600}>
                  Fair Use
                </Text>
                <Badge size="xs" variant="light" color={customer.fairUseIsOwn ? 'blue' : 'gray'}>
                  {customer.fairUseIsOwn ? 'Eigene Vereinbarung' : 'Hausstandard'}
                </Badge>
              </Group>
              {customer.fairUseSummary && (
                <Text size="xs" c="dimmed" mt={4}>
                  {customer.fairUseSummary}
                </Text>
              )}
            </div>
            <Button
              size="compact-xs"
              variant="light"
              color="gray"
              style={{ flexShrink: 0 }}
              onClick={() => setFairUseOpen(true)}
            >
              Öffnen
            </Button>
          </Group>
        </Paper>
      </Stack>

      <FairUseModal
        opened={fairUseOpen}
        onClose={() => setFairUseOpen(false)}
        customerId={customer.id}
        customerName={customer.name}
        policy={customer.fairUse}
        isOwn={customer.fairUseIsOwn}
        includedHoursPerMonth={customer.includedHoursPerMonth}
        canSeeCalculation={customer.fairUseCalculationVisible}
        queryKey={queryKey}
      />
    </InfoCard>
  );
}

function PortfolioStat({ label, value, color }: { label: string; value: number | undefined; color: string }) {
  return (
    <Paper radius="sm" p="xs" style={INSET}>
      <Text size="xl" fw={700} lh={1.1} c={value ? color : 'dimmed'}>
        {value ?? '–'}
      </Text>
      <Text size="xs" c="dimmed" mt={2}>
        {label}
      </Text>
    </Paper>
  );
}

/** Leistungsabgleich — was hat der Kunde, was wurde mit ihm noch nie besprochen. */
export function PortfolioCard({ ident }: { ident: CustomerInfoIdent }) {
  const { data } = useCustomerCare(ident);
  const customerId = data?.customer.id;
  const { data: portfolio } = useQuery({
    queryKey: ['customer-portfolio', customerId],
    queryFn: () => api.getCustomerPortfolio(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);

  if (!data) return null;
  const summary = portfolio?.summary;

  return (
    <InfoCard
      icon={<IconChecklist size={15} />}
      color="teal"
      title="Leistungen"
      hint="Abgleich mit dem FixByte-Portfolio: was beim Kunden läuft, was vorgemerkt ist — und was mit ihm noch nie besprochen wurde."
      actions={
        <Button size="compact-xs" variant="light" onClick={() => setOpen(true)}>
          Abgleich öffnen
        </Button>
      }
    >
      <SimpleGrid cols={3} spacing="xs">
        <PortfolioStat label="Läuft" value={summary?.active} color="teal" />
        <PortfolioStat label="Anbieten" value={summary?.proposed} color="blue" />
        <PortfolioStat label="Nie besprochen" value={summary?.unrated} color="orange" />
      </SimpleGrid>
      <PortfolioMatchModal
        opened={open}
        onClose={() => setOpen(false)}
        customerId={data.customer.id}
        customerName={data.customer.name}
      />
    </InfoCard>
  );
}
